import {
  migrateLegacyPortalPath,
  isPortalBrowserPath,
  portalBasePath,
  portalSectionFromPath,
} from "./portalPaths";

export const LAST_PATH_KEY = "taistu_last_path";
export const RETURN_PATH_KEY = "taistu_return_path";

/** Paths that should not be restored after sign-in or visiting the site root. */
export function isPersistablePath(path) {
  if (!path || path === "/" || path.startsWith("/verify")) return false;
  if (path.startsWith("/onboarding")) return false;
  if (path.startsWith("/portal/files/view/")) return false;
  if (path.startsWith("/portal/files/packet/")) return false;
  if (path.startsWith("/portal/files/scope")) return false;
  const seg = path.split("/").filter(Boolean)[0];
  if (seg && path.includes("/files/view/") && !["admin", "verify", "onboarding", "portal", "api"].includes(seg.toLowerCase())) {
    return false;
  }
  if (seg && path.includes("/files/packet/") && !["admin", "verify", "onboarding", "portal", "api"].includes(seg.toLowerCase())) {
    return false;
  }
  if (seg && /\/files\/scope\/?$/.test(path) && !["admin", "verify", "onboarding", "portal", "api"].includes(seg.toLowerCase())) {
    return false;
  }
  return true;
}

export function readLastPath() {
  if (typeof window === "undefined") return "";
  const stored = localStorage.getItem(LAST_PATH_KEY) || "";
  return isPersistablePath(stored) ? stored : "";
}

export function saveLastPath(path) {
  if (typeof window === "undefined") return;
  if (!isPersistablePath(path)) return;
  localStorage.setItem(LAST_PATH_KEY, path);
}

export function saveReturnPath(path) {
  if (typeof window === "undefined") return;
  const target = path ?? `${window.location.pathname}${window.location.search || ""}`;
  if (!isPersistablePath(target)) return;
  try {
    sessionStorage.setItem(RETURN_PATH_KEY, target);
  } catch {
    /* ignore */
  }
}

export function consumeReturnPath() {
  if (typeof window === "undefined") return "";
  try {
    const value = sessionStorage.getItem(RETURN_PATH_KEY) || "";
    sessionStorage.removeItem(RETURN_PATH_KEY);
    return value;
  } catch {
    return "";
  }
}

function hasActiveAdminPortalClient() {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(localStorage.getItem("taistu_active_client"));
  } catch {
    return false;
  }
}

function normalizeStoredPortalPath(path, client) {
  if (!path || !client) return path;
  if (path.startsWith("/portal")) return migrateLegacyPortalPath(path, client);
  return path;
}

function isClientHomePath(path) {
  if (!path) return false;
  if (path.startsWith("/portal")) {
    const rest = path.replace(/^\/portal\/?/, "");
    return !rest || rest.startsWith("?");
  }
  if (!isPortalBrowserPath(path)) return false;
  const section = portalSectionFromPath(path.split("?")[0] || path);
  return !section;
}

export function pathAllowedForUser(path, user) {
  if (!user || !path) return false;
  if (!isPersistablePath(path)) return false;
  if (path.startsWith("/portal")) {
    if (user.role === "admin") return hasActiveAdminPortalClient();
    return user.role === "client" && isClientHomePath(path);
  }
  if (isPortalBrowserPath(path)) {
    if (user.role === "admin") return hasActiveAdminPortalClient();
    return user.role === "client" && isClientHomePath(path);
  }
  if (user.role === "admin" && path.startsWith("/admin")) return true;
  return false;
}

export function resolvePostAuthPath(user, client) {
  const fromReturn = consumeReturnPath();
  if (pathAllowedForUser(fromReturn, user)) {
    return normalizeStoredPortalPath(fromReturn, client);
  }
  const last = readLastPath();
  if (pathAllowedForUser(last, user)) {
    return normalizeStoredPortalPath(last, client);
  }
  if (user.role === "admin") return "/admin";
  if (client) return portalBasePath(client);
  return "/";
}
